import { useRef, useEffect } from "react";

type ExperiencePreviewGalleryProps = {
  images: any[];
  eventsScroll: boolean;
};

export function ExperiencePreviewGallery({ images, eventsScroll }: ExperiencePreviewGalleryProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // ---- DRAG TO SCROLL ----
  useEffect(() => {
    if(!eventsScroll) return;
    const el = scrollRef.current;
    if (!el) return;

    let velX = 0;
    let lastX = 0;
    let lastTime = 0;
    let momentumId: number | null = null;

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      isDown.current = true;
      el.classList.add("dragging");
      startX.current = e.pageX - el.offsetLeft;
      scrollLeft.current = el.scrollLeft;

      lastX = e.pageX;
      lastTime = Date.now();
      velX = 0;

      if (momentumId) {
        cancelAnimationFrame(momentumId);
        momentumId = null;
      }
    };

    const startMomentum = () => {
      const timeSinceLastMove = Date.now() - lastTime;
      if (timeSinceLastMove > 100) {
        velX = 0;
      }

      const friction = 0.95;
      const step = () => {
        if (Math.abs(velX) < 0.5) {
          el.classList.remove("dragging");
          return;
        }
        el.scrollLeft -= velX;
        velX *= friction;
        momentumId = requestAnimationFrame(step);
      };
      momentumId = requestAnimationFrame(step);
    };

    const onMouseLeave = () => {
      if (!isDown.current) return;
      isDown.current = false;
      startMomentum();
    };

    const onMouseUp = () => {
      if (!isDown.current) return;
      isDown.current = false;
      startMomentum();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown.current) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX.current) * 1.5; // sensibilidad
      el.scrollLeft = scrollLeft.current - walk;

      const now = Date.now();
      const dt = now - lastTime;
      if (dt > 0) {
        const dx = e.pageX - lastX;
        velX = dx * 1.5; // match walk sensitivity multiplier
        lastX = e.pageX;
        lastTime = now;
      }
    };

    el.addEventListener("mousedown", onMouseDown);
    el.addEventListener("mouseleave", onMouseLeave);
    el.addEventListener("mouseup", onMouseUp);
    el.addEventListener("mousemove", onMouseMove);

    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      el.removeEventListener("mouseleave", onMouseLeave);
      el.removeEventListener("mouseup", onMouseUp);
      el.removeEventListener("mousemove", onMouseMove);
      if (momentumId) cancelAnimationFrame(momentumId);
    };
  }, [eventsScroll]);

  // ---- SCROLL CON RUEDA (VERTICAL → HORIZONTAL) ----
  useEffect(() => {
    if(!eventsScroll) return;
    const el = scrollRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      const isScrollEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      const isScrollStart = el.scrollLeft <= 0;

      // Only scroll horizontally and prevent default vertical page scroll
      // if there is actually room to scroll inside the carousel in that direction.
      if ((e.deltaY > 0 && !isScrollEnd) || (e.deltaY < 0 && !isScrollStart)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [eventsScroll]);

  // ---- BOTONES SIGUIENTE / ANTERIOR ----
  const scrollByAmount = 320; // tamaño aproximado de una imagen

  const handlePrev = () => {
    scrollRef.current?.scrollBy({ left: -scrollByAmount, behavior: "smooth" });
  };

  const handleNext = () => {
    scrollRef.current?.scrollBy({ left: scrollByAmount, behavior: "smooth" });
  };

  return (
    <div className="experience-preview-gallery-wrapper">
      <button className={`experience-preview-arrow left ${eventsScroll ? 'd-flex' : 'd-none'}`} onClick={handlePrev}>
        ‹
      </button>

      <div ref={scrollRef} className={`${eventsScroll ? 'experience-preview-gallery-carousel' : 'experience-preview-gallery-no-scroll' }`}>
        {(images ?? []).map((src, index) => (
          <div key={index} className="experience-preview-gallery-carousel-item">
            <img
              className="experience-preview-gallery-image"
              src={src?.image || src}
              alt={`Gallery image ${index + 1}`}
              draggable={false}
              decoding="async"
            />
          </div>
        ))}
      </div>

      <button className={`experience-preview-arrow right ${eventsScroll ? 'd-flex' : 'd-none'}`} onClick={handleNext}>
        ›
      </button>
    </div>
  );
}
